import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useScannerContext } from "@/hooks/scanner-context";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import useLineItemsState, {
  LineItemStateKey,
} from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { useRefundLineItems } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context";
import { useReturnValidationContext } from "@/pages/(app)/returns/view/utils/return-validation-context";
import useGetItemsInActiveReturns, {
  ItemInActiveReturn,
} from "@babackapp/private-api-client/client/use-get-items-in-active-returns";
import useEditLineItem from "@babackapp/private-api-client/client/use-edit-line-item";
import { getLineItemNotTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-logistician-quantity";
import { getLineItemNotReturnedQuantity } from "@babackapp/calculations/get-line-item-not-returned-quantity";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician } from "@babackapp/calculations/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-with-workflow-logistician";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician } from "@babackapp/calculations/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-without-workflow-logistician";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { toast } from "@babackapp/ui/components/sonner";
import useTranslation from "@babackapp/translations/use-translation";
import { useEffect, useRef } from "react";

const statesWithActiveReturnConflictCheck = new Set<LineItemStateKey>([
  "not-treated-by-merchant",
  "not-returned",
]);

export function getScannableLineItemStateKey(
  lineItem: ReturnRequestLineItemDto,
  workflowLogisticianActivated: boolean,
): LineItemStateKey | null {
  if (
    lineItem.quantity === 0 &&
    getLineItemNotReturnedQuantity({ lineItem }) > 0
  ) {
    return "not-returned";
  }

  if (workflowLogisticianActivated) {
    if (getLineItemNotTreatedByLogisticianQuantity({ lineItem }) > 0) {
      return "not-treated-by-logistician";
    }
    if (
      getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician(
        { lineItem },
      ) > 0
    ) {
      return "validated-by-logistician";
    }
    return null;
  }

  if (
    getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician(
      { lineItem },
    ) > 0
  ) {
    return "not-treated-by-merchant";
  }
  return null;
}

export function getScannerAdjustedMaxQuantity({
  lineItemId,
  stateKey,
  maxQuantity,
  itemsInActiveReturns,
}: {
  lineItemId: string;
  stateKey: LineItemStateKey;
  maxQuantity: number;
  itemsInActiveReturns: ItemInActiveReturn[] | undefined;
}): number {
  if (!statesWithActiveReturnConflictCheck.has(stateKey)) {
    return maxQuantity;
  }

  const quantityInOtherReturns =
    itemsInActiveReturns
      ?.filter((item) => item.lineItemId === lineItemId)
      .reduce((sum, item) => sum + item.quantity, 0) ?? 0;

  return Math.max(0, maxQuantity - quantityInOtherReturns);
}

export function useArticleScanner() {
  const { registerInterceptor } = useScannerContext();
  const t = useTranslation();
  const returnRequest = useCurrentReturnRequest();
  const { data: shop } = useCurrentShopSuspense();
  const { isOpen, openDialog } = useReturnValidationContext();
  const { lineItemsState, setLineItemState, resetAllQuantities } =
    useLineItemsState();
  const { items, setItems } = useRefundLineItems();
  const { data: itemsInActiveReturns } = useGetItemsInActiveReturns({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
  });
  const { mutate: editLineItem } = useEditLineItem({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
    onSuccess: (updatedReturnRequest) => {
      setItems(updatedReturnRequest.line_items);
    },
  });

  const isOpenRef = useRef(isOpen);
  const lineItemsStateRef = useRef(lineItemsState);
  const returnRequestRef = useRef(returnRequest);
  const workflowLogisticianRef = useRef(shop.workflowLogistician.activated);
  const itemsRef = useRef(items);
  const itemsInActiveReturnsRef = useRef(
    itemsInActiveReturns?.itemsInOtherReturns,
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    lineItemsStateRef.current = lineItemsState;
  }, [lineItemsState]);
  useEffect(() => {
    returnRequestRef.current = returnRequest;
  }, [returnRequest]);
  useEffect(() => {
    workflowLogisticianRef.current = shop.workflowLogistician.activated;
  }, [shop.workflowLogistician.activated]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    itemsInActiveReturnsRef.current = itemsInActiveReturns?.itemsInOtherReturns;
  }, [itemsInActiveReturns?.itemsInOtherReturns]);

  const setLineItemStateRef = useRef(setLineItemState);
  setLineItemStateRef.current = setLineItemState;
  const resetAllQuantitiesRef = useRef(resetAllQuantities);
  resetAllQuantitiesRef.current = resetAllQuantities;
  const openDialogRef = useRef(openDialog);
  openDialogRef.current = openDialog;
  const setItemsRef = useRef(setItems);
  setItemsRef.current = setItems;
  const editLineItemRef = useRef(editLineItem);
  editLineItemRef.current = editLineItem;

  const setDefaultResolutionRef = useRef(
    (lineItem: ReturnRequestLineItemDto) => {
      if (lineItem.resolution) return;

      setItemsRef.current(
        itemsRef.current.map((item) =>
          item.line_item_id === lineItem.line_item_id
            ? { ...item, resolution: "refund" }
            : item,
        ),
      );
      editLineItemRef.current({
        lineItemId: lineItem.line_item_id,
        requestBody: { resolution: "refund" },
      });
    },
  );

  useEffect(() => {
    const interceptor = (code: string): boolean => {
      const lineItems = returnRequestRef.current.line_items;
      const state = lineItemsStateRef.current;
      const popupOpen = isOpenRef.current;
      const workflowLogisticianActivated = workflowLogisticianRef.current;

      const belongsToReturn = lineItems.some(
        (lineItem) =>
          lineItem.variant?.barcode === code || lineItem.sku === code,
      );

      const matched = lineItems.find((lineItem) => {
        const matchesCode =
          lineItem.variant?.barcode === code || lineItem.sku === code;
        if (!matchesCode) return false;
        const stateKey = getScannableLineItemStateKey(
          lineItem,
          workflowLogisticianActivated,
        );
        if (!stateKey) return false;
        if (
          statesWithActiveReturnConflictCheck.has(stateKey) &&
          !itemsInActiveReturnsRef.current
        ) {
          return false;
        }

        const itemState = state[lineItem.line_item_id][stateKey];
        const adjustedMaxQuantity = getScannerAdjustedMaxQuantity({
          lineItemId: lineItem.line_item_id,
          stateKey,
          maxQuantity: itemState.maxQuantity,
          itemsInActiveReturns: itemsInActiveReturnsRef.current,
        });
        const selectedQuantity = popupOpen ? itemState.selectedQuantity : 0;

        return selectedQuantity < adjustedMaxQuantity;
      });

      if (!matched) {
        if (popupOpen && !belongsToReturn) {
          toast.error(t("scannerMode.unexpectedArticle"));
        }
        return popupOpen || belongsToReturn;
      }

      const stateKey = getScannableLineItemStateKey(
        matched,
        workflowLogisticianActivated,
      ) as LineItemStateKey;

      if (!popupOpen) {
        resetAllQuantitiesRef.current();
        setLineItemStateRef.current(matched.line_item_id, stateKey, 1);
        if (stateKey === "not-returned") {
          setDefaultResolutionRef.current(matched);
        }
        openDialogRef.current({ openedByScanner: true, stateKey });
      } else {
        const itemState = state[matched.line_item_id][stateKey];
        const adjustedMaxQuantity = getScannerAdjustedMaxQuantity({
          lineItemId: matched.line_item_id,
          stateKey,
          maxQuantity: itemState.maxQuantity,
          itemsInActiveReturns: itemsInActiveReturnsRef.current,
        });
        if (itemState.selectedQuantity < adjustedMaxQuantity) {
          setLineItemStateRef.current(
            matched.line_item_id,
            stateKey,
            itemState.selectedQuantity + 1,
          );
          if (stateKey === "not-returned") {
            setDefaultResolutionRef.current(matched);
          }
        }
      }

      return true;
    };

    return registerInterceptor(interceptor);
  }, [registerInterceptor]);
}
